import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { usePriceListCreateMutation, usePriceListFileMutation } from 'api';
import { Form, notification } from 'components';
import { dateFormatter } from 'constants/index';
import { ErrorMappingValueType } from 'typings';

export const useAddEdit = ({ onCancel }: { onCancel: () => void }) => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const [createPricelist, { isLoading: createIsLoading }] = usePriceListCreateMutation();
  const [createPricelistFile, { isLoading: createFileIsLoading }] = usePriceListFileMutation();
  const [notificationApi, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const handleErrors = useCallback(
    (error: any) => {
      if (!error?.data?.success && error?.data?.data) {
        Object.values(error.data.data).forEach((message) => {
          openNotificationWithIcon({
            type: 'error',
            message: message as string,
          });
        });
      } else {
        openNotificationWithIcon({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    },
    [openNotificationWithIcon]
  );

  const handleSubmit = useCallback(async () => {
    try {
      const { priselist, expiryDate, supplierId } = form.getFieldsValue();
      // TODO brandIds list of string
      const payload = {
        brandIds: [id],
        supplierId,
        expireDate: dateFormatter(expiryDate, 'end'),
      };
      const formData = new FormData();
      if (priselist?.length && id) {
        const file = priselist[0];
        formData.append('files', file.originFileObj, file.name);
      }
      const response = await createPricelist(payload);
      if (response?.data?.success) {
        if (formData.has('files')) {
          formData.append('priceListId', response.data.data);
          const fileResponse = await createPricelistFile(formData);
          if (fileResponse?.data?.success) {
            form.resetFields();
            openNotificationWithIcon({
              type: 'success',
              message: 'Pricelist was successfully created.',
            });
            onCancel();
          } else {
            handleErrors(fileResponse.error);
          }
        } else {
          openNotificationWithIcon({
            type: 'success',
            message: 'Pricelist was successfully created.',
          });
          onCancel();
        }
      } else {
        handleErrors(response.error);
      }
    } catch (err) {
      handleErrors(err);
    }
  }, [form, createPricelist, createPricelistFile, id, onCancel, openNotificationWithIcon, handleErrors]);

  return { contextHolder, form, handleSubmit, isLoading: createIsLoading || createFileIsLoading };
};
